import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FaUserShield, FaArrowLeft, FaCar, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaKey } from 'react-icons/fa'
import { supabase } from '../supabaseClient'
import '../styles/styles.css'

export default function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isValidReset, setIsValidReset] = useState(false)
  const [checkingReset, setCheckingReset] = useState(true)

  // Obtener los parámetros de la URL de Supabase
  const accessToken = searchParams.get('access_token')
  const refreshToken = searchParams.get('refresh_token')
  const type = searchParams.get('type')

  useEffect(() => {
    const validateResetToken = async () => {
      // Verificar si hay parámetros de reset de Supabase
      if (!accessToken || type !== 'recovery') {
        setErrorMsg('Link de reset no válido o expirado')
        setIsValidReset(false)
        setCheckingReset(false)
        return
      }

      try {
        // Intentar establecer la sesión con el token de acceso
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        })

        if (error) {
          console.error('❌ Error al validar token de reset:', error)
          setErrorMsg('Link de reset inválido o expirado')
          setIsValidReset(false)
        } else {
          console.log('✅ Token de reset válido')
          setIsValidReset(true)
        }
      } catch (error) {
        console.error('❌ Error inesperado al validar reset:', error)
        setErrorMsg('Error al validar el link de reset')
        setIsValidReset(false)
      } finally {
        setCheckingReset(false)
      }
    }

    validateResetToken()
  }, [accessToken, refreshToken, type])

  const handleResetPassword = async (e) => {
    e.preventDefault()
    setErrorMsg('')
    setSuccessMsg('')

    // Validaciones
    if (password !== confirmPassword) {
      setErrorMsg('Las contraseñas no coinciden')
      return
    }

    if (password.length < 6) {
      setErrorMsg('La contraseña debe tener al menos 6 caracteres')
      return
    }

    setLoading(true)

    try {
      console.log('🔄 Restableciendo contraseña...')
      
      // Actualizar la contraseña usando el token de reset
      const { data, error } = await supabase.auth.updateUser({
        password: password
      })

      if (error) {
        console.error('❌ Error al restablecer contraseña:', error)
        setErrorMsg(error.message)
        setLoading(false)
        return
      }

      // Si el reset es exitoso
      if (data.user) {
        console.log('✅ Contraseña restablecida exitosamente')
        setSuccessMsg('Contraseña restablecida exitosamente. Serás redirigido al login.')
        
        // Cerrar sesión para que el usuario tenga que hacer login con la nueva contraseña
        await supabase.auth.signOut()
        
        // Redirigir al login después de 2 segundos
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      }
    } catch (error) {
      console.error('❌ Error inesperado:', error)
      setErrorMsg('Error al restablecer la contraseña.')
    } finally {
      setLoading(false)
    }
  }

  const handleBack = () => {
    navigate('/login')
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword)
  }

  if (checkingReset) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="loading-spinner"></div>
          <p>Verificando link de reset...</p>
        </div>
      </div>
    )
  }

  if (!isValidReset) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <button onClick={handleBack} className="back-button">
              <FaArrowLeft />
              Volver
            </button>
          </div>

          <div className="login-logo">
            <div className="logo-icon">
              <FaCar />
            </div>
            <h1>LUBRICENTROS</h1>
          </div>

          <div className="login-title-section">
            <div className="user-icon">
              <FaUserShield />
            </div>
            <h2>Link Inválido</h2>
            <p>{errorMsg}</p>
          </div>

          <div className="login-footer">
            <p>Solicita un nuevo link de restablecimiento de contraseña desde la página de login</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header */}
        <div className="login-header">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft />
            Volver
          </button>
        </div>

        {/* Logo y título */}
        <div className="login-logo">
          <div className="logo-icon">
            <FaCar />
          </div>
          <h1>LUBRICENTROS</h1>
        </div>

        {/* Título principal */}
        <div className="login-title-section">
          <div className="user-icon">
            <FaKey />
          </div>
          <h2>Restablecer Contraseña</h2>
          <p>Ingresa tu nueva contraseña</p>
        </div>

        {/* Formulario */}
        <form className="login-form" onSubmit={handleResetPassword}>
          {/* Nueva Password */}
          <div className="form-field">
            <label>Nueva Contraseña</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Ingresa tu nueva contraseña"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="password-toggle"
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
          </div>

          {/* Confirmar Nueva Password */}
          <div className="form-field">
            <label>Confirmar Nueva Contraseña</label>
            <div className="input-wrapper">
              <FaLock className="input-icon" />
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirma tu nueva contraseña"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="password-toggle"
              >
                {showConfirmPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </button>
            </div>
          </div>
          
          {/* Error message */}
          {errorMsg && (
            <div className="error-message">
              <FaLock />
              {errorMsg}
            </div>
          )}

          {/* Success message */}
          {successMsg && (
            <div className="success-message">
              <FaKey />
              {successMsg}
            </div>
          )}
          
          {/* Submit button */}
          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? (
              <>
                <div className="loading-spinner"></div>
                Restableciendo...
              </>
            ) : (
              <>
                <FaKey />
                Restablecer Contraseña
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="login-footer">
          <p>Sistema de gestión de lubricentros</p>
        </div>
      </div>
    </div>
  )
} 